import { BaseTypeIds } from "@/components/ui/jobs/JobDetails/CreateQuote/helpers";
import { AppState } from "@/models/TechnicianAppState";
import { MultupleListItem } from "@/models/Common";
import { QuoteDevice, QuoteMode, QuotePart, QuoteService, QuoteState, QuoteImageItem, QuoteCustomerDeviceAttributes, DeviceAttributeslItem, AttributesInputTypes, JobChecklist, DeviceCheckList, CheckListDetailItem, PriceIngredient, ComputeData } from "@/models/QuoteState";
import utils from "@/utils/utils";

export const QuoteGetters = {
	GET_IS_IMAGE_UPLOAD: 'GET_IS_IMAGE_UPLOAD',
	GET_QUOTE_DEVICES_MAP: 'GET_QUOTE_DEVICES_MAP',
	GET_QUOTE_TOTAL_PRICE: 'GET_QUOTE_TOTAL_PRICE',
	IS_IMAGES_UPLOADED_VALID: 'IS_IMAGES_UPLOADED_VALID',
	IS_ATTRIBUTES_VALID: 'IS_ATTRIBUTES_VALID',
	IS_PARTS_AND_SERVICES_VALID: 'IS_PARTS_AND_SERVICES_VALID',
	IS_VALID_FOR_QUOTE_CREATION: 'IS_VALID_FOR_QUOTE_CREATION',
	JOB_IMAGES: 'JOB_IMAGES',
	QUOTE_IMAGES: 'QUOTE_IMAGES',
	QUOTE_ATTRIBUTES: 'QUOTE_ATTRIBUTES',
	IS_IN_SERVICES_EDIT_MODE: 'IS_IN_SERVICES_EDIT_MODE',
	GET_QUOTE_DEVICES_LIST: 'GET_QUOTE_DEVICES_LIST',
	PROPERTY_IMAGES: 'PROPERTY_IMAGES',
	DEVICE_CHECKLIST: 'DEVICE_CHECKLIST',
	IS_CHECKLISTS_VALID: 'IS_CHECKLISTS_VALID',
	DEVICE_FREE_SERVICES: 'DEVICE_FREE_SERVICES',
	COMPUTE_DATA: 'COMPUTE_DATA',
	PRICE_INGREDIENTS_MAP: 'PRICE_INGREDIENTS_MAP',
	COMPUTED_TOTAL_PRICE: 'COMPUTED_TOTAL_PRICE'
}

export default {
	[QuoteGetters.JOB_IMAGES]: (state: QuoteState): QuoteImageItem[] => {
		return state.jobImages;
	},
	[QuoteGetters.PROPERTY_IMAGES]: (state: QuoteState): QuoteImageItem[] => {
		return state.propertyImages;
	},
	[QuoteGetters.GET_IS_IMAGE_UPLOAD]: (state: QuoteState): QuoteMode => {
		return state.mode;
	},
	[QuoteGetters.QUOTE_IMAGES]: (state: QuoteState): QuoteImageItem[] => { 
		return state.quoteImages;
	},
	[QuoteGetters.GET_QUOTE_DEVICES_LIST]: (state: QuoteState): QuoteDevice[] => {
		return state.quoteDevices
	},
	[QuoteGetters.GET_QUOTE_DEVICES_MAP]: (state: QuoteState): { [key: string]: QuoteDevice} => {
		return state.quoteDevices.reduce((acc: any, device: QuoteDevice) => {
			acc[device.customerDeviceId] = device;
			return acc;
		}, {});
	},
	[QuoteGetters.GET_QUOTE_TOTAL_PRICE]: (state: QuoteState): number => {
		return state.quoteDevices.reduce((sum: number, device: QuoteDevice) => {
			return sum + utils.getQuoteDevicePrice(device);
		}, 0)
	},
	[QuoteGetters.IS_IMAGES_UPLOADED_VALID]: (state: QuoteState): boolean => {
		return isImagesValid(state);
	},
	[QuoteGetters.IS_ATTRIBUTES_VALID]: (state: QuoteState): boolean => {
		return isAttributesValid(state);
	},
	[QuoteGetters.IS_PARTS_AND_SERVICES_VALID]: (state: QuoteState): boolean => {
		return isPartsAndServicesValid(state)
	},
	[QuoteGetters.IS_CHECKLISTS_VALID]: (state: QuoteState): boolean => {
		return isChecklistValid(state)
	},
	[QuoteGetters.IS_VALID_FOR_QUOTE_CREATION]: (state: QuoteState, getters: any, rootState: AppState): boolean => {
		if (rootState.jobs.current_job?.details.is_follow_up) {
			return isPropertyImagesValid(state) && isDeviceChecklistValid(state.checklist?.post || []);
		}

		let imageValidation = isImagesValid;
		if (rootState.jobs.current_job?.details.is_diagnostic) { 
			imageValidation = isPropertyImagesValid;
		}

		return imageValidation(state) && isAttributesValid(state) && isPartsAndServicesValid(state) && !state.in_edit_mode && isDeviceChecklistValid(state.checklist?.pre || [])
	},
	[QuoteGetters.QUOTE_ATTRIBUTES]: (state: QuoteState): QuoteCustomerDeviceAttributes[] => {
		return state.attributes;
	},
	[QuoteGetters.IS_IN_SERVICES_EDIT_MODE]: (state: QuoteState): boolean => {
		return state.in_edit_mode;
	},
	[QuoteGetters.DEVICE_CHECKLIST]: (state: QuoteState): JobChecklist | null => {
		return state.checklist;
	},
	[QuoteGetters.DEVICE_FREE_SERVICES]: (state: QuoteState): PriceIngredient[] => {
		return (state.computeData?.ingredients || []).filter((ing: PriceIngredient) => ing.isAutoAdded || !ing.deviceId);
	},
	[QuoteGetters.COMPUTE_DATA]: (state: QuoteState): ComputeData | null => {
		return state.computeData;
	},
	[QuoteGetters.PRICE_INGREDIENTS_MAP]: (state: QuoteState, getters: any): { [key: string]: PriceIngredient} => {
		if (!state.computeData) return getters[QuoteGetters.GET_QUOTE_DEVICES_MAP];
		return state.computeData?.ingredients.reduce((acc: any, ing: PriceIngredient) => {
			if (!acc[ing.customerDeviceId]) {
				acc[ing.customerDeviceId] = {
					parts: {},
					services: {}
				}
			}

			const device = acc[ing.customerDeviceId];
			const targetObj = ing.baseTypeId === BaseTypeIds.SERVICE ? device.services : device.parts
			targetObj[ing.serviceId] = ing;
			
			return acc;
		}, {});
	},
	[QuoteGetters.COMPUTED_TOTAL_PRICE]: (state: QuoteState): number => {
		return (state.computeData?.totalPrice || 0) + (state.computeData?.tax || 0);
	}
}

function isImagesValid (state: QuoteState) {
	return isJobImagesValid(state) && isPropertyImagesValid(state);
}

function isJobImagesValid (state: QuoteState) {
	return state.jobImages.length >= 3;
}

function isPropertyImagesValid (state: QuoteState) {
	return state.propertyImages.length >= 1;
}

function isAttributesValid (state: QuoteState) {
	return state.attributes.every(isQuoteAttrValid)	
}

function isQuoteAttrValid (quoteAttr: QuoteCustomerDeviceAttributes) { 
	if (!quoteAttr.attributes) return true;
	return quoteAttr.attributes.every(isDeviceAttributeValid);
}

export function isDeviceAttributeValid (attribute: DeviceAttributeslItem) {
	if (!attribute.is_mandatory) return true;

	if (attribute.type === AttributesInputTypes.MULTIPLE_SELECTION) {
		const selectedOptions = ((attribute.options || []) as MultupleListItem[]).filter(opt => opt.selected);
		return !!selectedOptions.length || !!attribute.images.length
	} 
	return !!attribute.value || !!attribute.images.length
}

function isPartsAndServicesValid (state: QuoteState) {
	if (state.mode === QuoteMode.IMAGES && state.quoteImages.length) {
		return true;
	}

	if (!state.quoteDevices.length) {
		return false;
	}
	
	return state.quoteDevices.every(isQuoteDeviceValid)
}

function isQuoteDeviceValid (device: QuoteDevice) {
	return isDeviceServicesValid(device.services) && isDevicePartsValid(device.parts);
}

function isDeviceServicesValid (services: QuoteService[]) {
	if (!services.length) {
		return false;
	}

	return services.every(isServiceItemValid);
}

function isDevicePartsValid (parts: QuotePart[]) {
	return parts.every(isPartItemValid);
}

export function isPartItemValid (part: QuotePart) {
	return isServiceItemValid(part) && part.sku;
}

export function isServiceItemValid (service: QuoteService | QuotePart) {
	return service.title?.length && parseFloat(service.price) > 0
}

function isChecklistValid (state: QuoteState) {
	return isDeviceChecklistValid(state.checklist?.post || []) && isDeviceChecklistValid(state.checklist?.pre || [])
}

function isDeviceChecklistValid (checklists: DeviceCheckList[]) : boolean {
	return checklists.every((deviceChecklist: DeviceCheckList) => {
		return deviceChecklist.checklist_details.every((item: CheckListDetailItem) => item.note.length);
	});
}
